// Remove in APP-130299 - Disabling in the meantime so development can continue.

import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import some from 'lodash/some';
import momentTimeZone from 'moment-timezone';

import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';
import { get as gcsGet } from '@/data/gcs-service.js';
import VisitorsInGuideSegmentAggregation from '@/guides/aggregations/visitors-in-guide-segment.aggregation';
import { combineContent, generateCspContent, splitContent } from '@/guides/helpers/guide-content';
import { createGuide as buildGuide, createStep, createdDate } from '@/guides/helpers/guide-editor.util.js';
import { buildAudienceAndAudienceUiHint } from '@/orchestrate/journeys/helpers';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { isMobileApp } from '@/utils/apps';
import moment from '@/utils/moment-wrapper';
import { UserStorage } from '@/utils/storage';
import { isIdeaPollGuide, isNpsGuide } from '@/vuex/modules/guides/guides.utils';
import { filtersHaveRulesOfKind } from '@/vuex/modules/segments/segment.helpers';
import { http } from '@pendo/http';
import {
    BuildingBlock,
    BuildingBlockLayouts,
    BuildingBlockMigrations,
    BuildingBlockProperties,
    BuildingBlockPropertiesService,
    Polls,
    SdkTools,
    StepCopy,
    configureEmbed,
} from '@pendo/services/BuildingBlocks';
import { ACTIVATION_TYPES_ORDER, MESSAGE_TYPES } from '@pendo/services/Constants';
import { $can } from '@pendo/permissions';
import { operators as o } from '@pendo/aggregations';
import { isCrossApp } from '@pendo/services/CrossAppGuides';
import { request } from '@utils/request';
import { v4 as uuid } from 'uuid';
import { i18n_t as $t } from '@/utils/i18n';

const { EMAIL } = MESSAGE_TYPES;
const { findBlock, findDomBlock } = BuildingBlock;
const { widgetIds } = BuildingBlockLayouts;
const CAROUSEL_INDICATOR_WRAPPER = 'CarouselIndicatorWrapper';
const CAROUSEL_FIRST_STEP_LAYOUT_ID = '_PENDO_DEFLYT_MobileCarouselGuide';
const { getMinSdkVersionForGuide, ROOT_MIN_SDK_VERSION } = SdkTools;

export const carouselLayouts = {
    _PENDO_DEFLYT_MobileCarouselSecondStep: true,
    _PENDO_DEFLYT_MobileCarouselThirdStep: true,
};

export const GUIDE_STATE = {
    'archived': '_archived_',
    'deleted': '_deleted_',
    'disabled': 'disabled',
    'draft': 'draft',
    'pending-review': '_pendingReview_',
    'public': 'public',
    'scheduled': 'scheduled',
    'staged': 'staged',
};
const DESIGNER_URL_STORAGE_BASE_KEY = 'guide.designer.urls';
const normalizedUrlMap = {};

/* Type-related helpers */
export function getIsAuto (guide) {
    return /auto/.test(guide.launchMethod);
}

export function getIsBetaFlag (guide) {
    return (
        (!!find(get(guide, 'polls', []), (poll) => /^NPS/.test(get(poll, 'attributes.type', ''))) &&
            moment(guide.createdAt).isBefore(moment('2019-02-18T00:00:00.000'))) ||
        (get(guide, 'attributes.type', '') === 'building-block' &&
            moment(guide.createdAt).isBefore(moment('2019-02-18T00:00:00.000')))
    );
}

export function getIsBuildingBlock (guide) {
    return getType(guide) === 'building-block';
}

export function getIsBuildingBlockWalkthrough (guide) {
    const numSteps = guide.steps ? guide.steps.length : 0;

    return getIsBuildingBlock(guide) && numSteps > 1;
}

export function getIsGroup (guide) {
    return getType(guide) === 'group';
}

export function getIsGuideCenter (guide) {
    const { attributes } = guide;

    return !!(attributes && (attributes.guideType || attributes.resourceCenter));
}

export function getIsLauncher (guide) {
    return getType(guide) === 'launcher';
}

export function getIsMobileOnly (guide) {
    const attributes = guide.attributes || {};
    const device = attributes.device || {};

    return getType(guide) === 'mobile-lightbox' || !!(device.mobile && !device.desktop) || device.type === 'mobile';
}

export function getIsMulti (guide) {
    return getIsWalkthrough(guide) || getIsGroup(guide);
}

export function getIsWalkthrough (guide) {
    return getType(guide) === 'walkthrough' || getIsBuildingBlockWalkthrough(guide);
}

export function getLabel (guide) {
    if (getIsWalkthrough(guide)) {
        return 'Walkthrough';
    }
    if (getIsGroup(guide)) {
        return 'Guide Group';
    }

    return 'Guide';
}

export function getType (guide) {
    const attributesType = get(guide, 'attributes.type');
    if (attributesType) return attributesType;

    if (get(guide, 'isMultiStep')) return 'walkthrough';

    if (get(guide, 'steps.length')) return guide.steps[0].type;
}

export function getTypeString (guide) {
    const type = getType(guide);

    if (getIsBuildingBlock(guide)) {
        return 'Building Block';
    }
    if (getIsWalkthrough(guide)) {
        const count = guide.steps.length;

        return `Walkthrough (${count} step${count !== 1 ? 's' : ''})`;
    }
    if (getIsGroup(guide)) {
        const count = guide.steps.length;

        return `Guide Group (${count} guide${count !== 1 ? 's' : ''})`;
    }
    if (isHtmlGuide(guide)) {
        return 'HTML';
    }
    if (type === 'mobile-lightbox') {
        return 'Lightbox (mobile)';
    }
    if (type) {
        return type[0].toUpperCase() + type.slice(1);
    }

    return 'Invalid Guide';
}

export function isEmbeddedGuide (guide) {
    return get(guide, 'launchMethod') === 'embed';
}

export function isGuideExperimentable (guide) {
    return (
        getIsBuildingBlock(guide) &&
        !isHtmlGuide(guide) &&
        !isNpsGuide(guide) &&
        !guide.attributes.resourceCenter &&
        !guide.attributes.isAnnouncement &&
        !guide.attributes.isEmail &&
        !isCrossApp(guide) &&
        !getIsPartOfJourney(guide) &&
        !isDoNotTrackGuide(guide)
    );
}

export function isHtmlGuide (guide) {
    return guide?.editorType === 'html';
}

export function canEditGuideVisually (guide) {
    return !isHtmlGuide(guide);
}

/* End type-related helpers */

/* State-related helpers */
export function getIsArchived (guide) {
    return guide.state === GUIDE_STATE.archived;
}

export function getIsDeleted (guide) {
    return guide.state === GUIDE_STATE.deleted;
}

export function getIsDisabled (guide) {
    return guide.state === GUIDE_STATE.disabled;
}

export function getIsPublic (guide) {
    return guide.state === GUIDE_STATE.public;
}

export function getIsEnabled (guide) {
    return !getIsDisabled(guide) && !getIsDeleted(guide) && !getIsArchived(guide);
}

export function getIsOverrideThrottling (guide) {
    const { attributes } = guide;

    return !!(attributes && attributes.overrideAutoThrottling && getIsEnabled(guide));
}

export function getIsResourceCenter (guide) {
    return !!guide.attributes?.resourceCenter;
}

export function getIsResourceCenterHomeView (guide) {
    return !!guide.attributes?.resourceCenter?.isTopLevel;
}

export function getIsRegularGuide (guide) {
    const { attributes } = guide;

    if (guide.launchMethod === 'form') return false;

    return !(attributes?.resourceCenter || attributes?.isAnnouncement || attributes?.isEmail);
}

export function getIsPartOfJourney (guide) {
    return !!guide.attributes?.journeyId;
}

export function isDoNotTrackGuide (guide) {
    return !!guide.isDNT;
}

export function isDoNotTrackEnabledForApp (app) {
    return !!app?.applicationFlags?.doNotTrackGuides;
}

export function getCrossAppEligibleGuides (guides) {
    return guides.filter((guide) => !isDoNotTrackGuide(guide));
}

export function getIsValid (guide) {
    if (guide.state === GUIDE_STATE.draft || guide.state === GUIDE_STATE['pending-review']) return true;

    return !getIsLauncher(guide);
}

export function getIsListableGuide (guide, { hasNps, hasValidate }) {
    return hasProductLineAccess(guide, { hasNps, hasValidate }) && getIsRegularGuide(guide) && getIsValid(guide);
}

export function getIsGuideWithGoal (guide) {
    return has(guide, 'conversion');
}

/* End state-related helpers */

/* Poll-related helpers */
export function getHasPolls (guide) {
    return some(guide.steps, (step) => step.pollIds && step.pollIds.length);
}

export function getLivePolls (guide) {
    const livePolls = reduce(
        guide.steps,
        (pollIdsMap, step) => {
            if (step.pollIds) {
                step.pollIds.forEach((pollId) => {
                    pollIdsMap[pollId] = true;
                });
            }

            return pollIdsMap;
        },
        {},
    );

    return filter(guide.polls, (poll) => livePolls[poll.id]);
}

export function getVisiblePolls (guide) {
    // Same as livePolls, but excludes "hidden" poll types
    return reject(
        getLivePolls(guide),
        (poll) => poll.attributes && (poll.attributes.type === 'NPSReason' || poll.attributes.type === 'VoteVocItemId'),
    );
}

export function getPollTypeLabel (poll) {
    return {
        PickList: $t('pollTypes.Multiple Choice'),
        PositiveNegative: $t('pollTypes.Yes / No'),
        FreeForm: $t('pollTypes.Open Text Field'),
        NumberScale: $t('pollTypes.Number Scale'),
        NPSRating: $t('pollTypes.NPS Survey'),
        NPSReason: $t('pollTypes.NPS Reason'),
    }[poll.attributes.type];
}

export function getPollById (pollId, guide) {
    return guide.polls.find((el) => el.id === pollId);
}

export function getFormattedPollResponse (result, poll) {
    result = result === null || result === undefined ? '' : result;
    const type = poll && poll.attributes && poll.attributes.type;
    let idResponses = poll && poll.idResponses;
    const numericResponses = poll && poll.numericResponses;
    if (type === 'PositiveNegative') {
        idResponses = idResponses || { '1': 'Yes', '0': 'No' };

        return idResponses[result] || result;
    }
    if (type === 'NPSRating' && typeof result === 'string') {
        switch (result) {
            case 'ui:web:pollResponse':
            case 'ui:mobile:pollResponse':
                return 'In-app';
            case 'ui:email:pollResponse':
                return 'Email';
            default:
                return result;
        }
    } else if (numericResponses) {
        return result;
    } else if (idResponses) {
        return idResponses[result] || result;
    } else {
        return result;
    }
}
/* End poll-related helpers */

/* Steps-related helpers */
export function getStepsThatNeedReview ({ guide, templates = [] }) {
    const hasTemplates = guide.steps.some((step) => Boolean(step.templateId));
    if (hasTemplates && !getIsBuildingBlock(guide)) {
        return templates.flatMap((template) => getStepsThatNeedUpdate(guide, template));
    }

    return [];
}

function getStepsThatNeedUpdate (guide, template) {
    const stepsForTemplate = getStepsForTemplate(guide, template);

    return stepsForTemplate.filter((step) => doesStepNeedUpdate(step, template));
}

function getStepsForTemplate (guide, template) {
    const steps = guide.steps.filter((step) => step.templateId === template.id);

    return steps;
}

function doesStepNeedUpdate (step, template) {
    return step.lastUpdatedAt < template.lastUpdatedAt;
}

export function hasEverBeenEmailPoll (guide) {
    return !!guide.emailState;
}
/* End step-related helpers */

/* Resource center helpers */
export function buildSegmentFromGuide (guide, subscriptionSegments, defaultSegments) {
    const filters = get(guide, 'audienceUiHint.filters', []);
    const ruleCount = filters.length;
    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');
    const segmentId = get(guide, 'audienceUiHint.filters[0].segmentId');

    if (!isCustomNestedSegment && segmentId) {
        return find(subscriptionSegments.concat(defaultSegments), { id: segmentId });
    }

    if (ruleCount) {
        return {
            filters,
            pipeline: guide.audience,
            name: 'Custom Segment',
        };
    }

    return find(defaultSegments, { id: 'everyone' });
}

export function getNonResourceCenterGuide (guide) {
    return !guide.attributes?.resourceCenter;
}

/* End resource center helpers */

/* Misc. helpers */
/**
 * AngularJS files that need this function (particularly for $q functionality)
 * Returns entire guide object
 **/
export async function getContent (guide) {
    if (!guide?.steps) return;
    const stepsWithContent = await getGuideStepsContent(guide.steps);
    const guideWithContent = cloneDeep(guide);
    guideWithContent.steps = stepsWithContent;

    return guideWithContent;
}

// Returns guide steps only; getContent (above) returns entire guide with content
export async function getGuideStepsContent (steps) {
    return Promise.all(
        steps.map(async (step) => {
            if (!step.buildingBlocksUrl) {
                return step;
            }
            const buildingBlocks = await gcsGet(step.buildingBlocksUrl, { transformResponse: null });
            const dom = await gcsGet(step.domUrl, { transformResponse: null });
            const stepContent = {
                buildingBlocks,
                dom,
            };

            return Object.assign(step, stepContent);
        }),
    );
}

export function getDeviceType ({ app, attributes }) {
    if (app) {
        if (app.platform === 'ios') {
            return 'iOS';
        }
        if (app.platform === 'android') {
            return 'Android';
        }
    }

    const device = get(attributes, 'device');
    if (!device) {
        return;
    }
    if (device.desktop && device.mobile) {
        return 'Desktop, Mobile Web';
    }
    if (device.desktop) {
        return 'Desktop';
    }
    if (device.mobile) {
        return 'Mobile Web';
    }
    if (device.type) {
        const deviceType = get(device, 'type');
        switch (deviceType) {
            case 'desktop':
                return 'Desktop';
            case 'mobile':
                return 'Mobile Web';
            case 'all':
                return 'Desktop, Mobile Web';
        }
    }
}

export function getDeleteConfirmationType (guide) {
    return guide.launchMethod.indexOf('launcher') > -1 && guide.attributes.type === 'building-block';
}

export function getTemplateString ({ guide, templates = [] }) {
    let templateString = '';
    if (templates.length) {
        const templateMap = {};
        const templateCount = reduce(
            guide.steps,
            (count, step) => {
                if (!!step.templateId && !templateMap[step.templateId]) {
                    count++;
                    templateMap[step.templateId] = true;
                }

                return count;
            },
            0,
        );
        if (templateCount > 1) {
            templateString = `${templateCount} templates`;
        } else if (templateCount === 1) {
            const template = find(templates, ['id', Object.keys(templateMap)[0]]);
            if (template) templateString = template.name;
        }
    }

    return templateString;
}

export function launchMethodToString (launchMethod) {
    const activationTypeMapping = {
        auto: 'Automatic',
        badge: 'Badge',
        dom: 'Target Element',
        api: 'Api',
        launcher: 'Resource Center',
    };
    // if launch method is missing/empty/falsy treat it as api
    if (!launchMethod || launchMethod === 'api') return `${activationTypeMapping.api}`;

    const guideActivationTypes = launchMethod
        .split('-')
        .sort((a, b) => ACTIVATION_TYPES_ORDER.indexOf(a) - ACTIVATION_TYPES_ORDER.indexOf(b));
    const formattedTypes = guideActivationTypes.map((typeKey) => {
        if (!activationTypeMapping[typeKey]) return '';

        return activationTypeMapping[typeKey];
    });

    return formattedTypes.join(', ');
}

export function removeLaunchMethodFromGuide (guide, methodToRemove) {
    const launchMethods = guide.launchMethod.split('-');
    const newLaunchMethods = launchMethods.reduce((acc, method) => {
        if (method === methodToRemove) return acc;

        return acc.concat(method);
    }, []);

    guide.launchMethod = newLaunchMethods.join('-');
    if (guide.launchMethod === '') {
        guide.launchMethod = 'api';
    }

    return guide;
}

export function updateClonedGuideActivationLocation (guide, isMobileApp = false) {
    if (isMobileApp) {
        return updateClonedGuideActivation(guide);
    }

    return updateLocationToSitewide(guide);
}

export function updateClonedGuideActivation (guide) {
    const launchMethod = guide.launchMethod === 'auto' ? 'auto' : 'api';
    guide = changeLaunchMethod(guide, launchMethod);
    guide = changeAttributesCapping(guide);
    guide.steps = guide.steps.map((step, index) => {
        step = removeStepLocations(step);
        if (launchMethod === 'api') {
            if (index !== 0) {
                step = updateStepActivation(step, 'any');
            }
        } else {
            const activation = index === 0 ? 'appLaunch' : 'any';
            step = updateStepActivation(step, activation);
        }

        return step;
    });

    return guide;
}

export function changeLaunchMethod (guide, launchMethod) {
    return {
        ...guide,
        launchMethod,
    };
}

export function removeStepLocations (step) {
    delete step.pageId;
    delete step.featureId;
    delete step.locationFeatureId;
    delete step.activations;
    delete step.trackTypeIds;

    return step;
}

export function updateLocationToSitewide (guide) {
    guide.steps = guide.steps.map((step) => {
        delete step.pageId;
        delete step.regexUrlRule;

        return step;
    });

    return guide;
}

export function updateStepActivation (step, event) {
    return {
        ...step,
        activations: [
            {
                event,
            },
        ],
    };
}

export function changeAttributesCapping (guide) {
    return {
        ...guide,
        attributes: {
            ...guide.attributes,
            capping: {
                maxSessionImpressions: 1,
                maxImpressions: 1,
            },
        },
    };
}

export function getDesignerLocation (subId, appId = '-323232') {
    return getLocation(subId, appId);
}

function getLocationKey (subId, appId = '-323232') {
    return `${DESIGNER_URL_STORAGE_BASE_KEY}.${subId}.${appId}`;
}

function getAdoptLocationKey (subId, appId = '-323232') {
    return `${subId}:${appId}--lastDesignerUrl`;
}

async function getLocation (subId, appId) {
    const setting = await UserStorage.get(getLocationKey(subId, appId));
    if (setting.value) return setting;

    const settingFromAdopt = await UserStorage.get(getAdoptLocationKey(subId, appId));
    if (settingFromAdopt.value) return settingFromAdopt;

    return UserStorage.get(DESIGNER_URL_STORAGE_BASE_KEY);
}

export function generateDesignerLastUrlUserSettingsKey (subscriptionId, appId) {
    // this key doesn't match some of the more modern formatting we use in user settings because its ancient
    return `guide.designer.urls.${subscriptionId}.${appId}`;
}

export function setDesignerLocation (subId, appId, loc) {
    UserStorage.set(getLocationKey(subId, appId), loc);
}

export function isHTTPS (url) {
    return url.toLowerCase().indexOf('https://') === 0;
}

export function isHTTP (url) {
    return url.toLowerCase().indexOf('http://') === 0;
}

export function isChromeExtension (url) {
    return url.toLowerCase().indexOf('chrome-extension://') === 0;
}

export function validateURL (url) {
    if (!url) return false;

    if (!isHTTP(url) && !isHTTPS(url) && !isChromeExtension(url)) {
        return false;
    }

    return true;
}

export function findModuleGuideListBuildingBlock (buildingBlocks) {
    return findBlock(buildingBlocks, (block) => {
        return block.widget === widgetIds.unorderedListItem && block.templateChildren;
    });
}

export function findModuleGuideListDomBlock (dom) {
    return findDomBlock(dom, (block) => {
        return block.templateChildren;
    });
}

export function getGuideTargetingSegment (guide) {
    const filters = get(guide, ['audienceUiHint', 'filters'], []);

    if (guide.attributes?.journeyId) {
        return guide.audience;
    }

    if (isEmpty(filters)) {
        return null;
    }

    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');
    const targetingSegmentId = head(compact(filters.map((filter) => filter.segmentId)));
    const isNoOneSegment = targetingSegmentId === 'noOne';

    if (isNoOneSegment) {
        return guide.audience;
    }

    if (isCustomNestedSegment) {
        // A Custom nested segment requires us to use a compound expression instead of a segmentId
        // or a normal pipeline. To prevent the UI from running additional layers of pipelines in
        // the agg this is consumed in, we are stripping out the compound expression from the audience.
        return guide.audience.find((operator) => operator.segment).segment;
    }

    // the guide's "filter" (segment) can either be a segmentId or an agg pipeline
    if (targetingSegmentId && filters.length === 1) {
        return targetingSegmentId;
    }

    return [...guide.audience];
}

// this is used in places where one only ever cares about the non-segmented count
export async function getGuideSegmentVisitorCount ({ guide, aggParams }) {
    aggParams.guide = guide;

    const pipeline = VisitorsInGuideSegmentAggregation.build(aggParams);
    const aggConfig = {
        name: 'GuideDetails--VisitorsInGuideSegment',
        productArea: PRODUCT_AREAS.GUIDES,
        subProductArea: SUB_PRODUCT_AREAS.GUIDE_METRICS,
    };

    const [result] = await request(o.aggregation(aggConfig, pipeline));

    return result;
}

/* The default distance between step ranks */
export const RANK_INCREMENT = 5000000;

export function guideIsUndeletable (guide) {
    const {
        'public': PUBLIC,
        'staged': STAGED,
        'scheduled': SCHEDULED,
        'draft': DRAFT,
        'pending-review': PENDING_REVIEW,
        'disabled': DISABLED,
    } = GUIDE_STATE;

    return [PUBLIC, STAGED, SCHEDULED, DRAFT, PENDING_REVIEW, DISABLED].includes(guide.state);
}

export function guideSegmentName (guide, segment, segmentNameFallback) {
    const filters = get(guide, 'audienceUiHint.filters', []);
    const ruleCount = filters.length;
    const segmentId = filters[0]?.segmentId;
    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

    if (!isCustomNestedSegment && segmentId) {
        return get(segment, 'name', segmentNameFallback);
    }

    if (ruleCount) {
        return 'Custom';
    }

    return 'Everyone';
}

export async function buildAudiencePipeline (segment, targetApp) {
    return buildAudienceAndAudienceUiHint(segment, targetApp);
}

export function normalizeDateTime (date, time = '9:00am') {
    const formattedDate = momentTimeZone(date).format('YYYY-MM-DD HH:mm:ss');
    if (!date) {
        return; // if no date, return undefined
    }

    if (!time) {
        // catch null or an empty string
        time = '9:00am';
    }

    const isPm = time.includes('pm');
    let [hour, minute] = time.replace(/am|pm/, '').split(':');

    hour = parseInt(hour, 10);
    minute = parseInt(minute, 10);

    if (hour === 12) {
        if (!isPm) {
            hour = 0;
        }
    } else if (isPm) {
        hour += 12;
    }
    const normalized = momentTimeZone
        .tz(formattedDate, momentTimeZone.tz.guess())
        .hours(hour)
        .minutes(minute)
        .seconds(0);

    return normalized.valueOf(); // convert to ms since unix epoch
}

export function dateOneIsEarlierOrSame (dateOne, timeOne, dateTwo, timeTwo) {
    let dateTimeOne;
    let dateTimeTwo;
    if (dateOne) {
        dateTimeOne = normalizeDateTime(dateOne, timeOne);
    }
    if (dateTwo) {
        dateTimeTwo = normalizeDateTime(dateTwo, timeTwo);
    }

    return dateTimeOne && dateTimeTwo && momentTimeZone(dateTimeOne).isSameOrBefore(dateTimeTwo);
}

export function getGuideCreationDate (guide) {
    const isoDate = 'YYYY-MM-DD';
    const today = moment().startOf('day');
    const createdAt = moment(guide.createdAt).startOf('day');

    return {
        start: createdAt.format(isoDate),
        endPlusOneDay: today.add(1, 'day').format(isoDate),
    };
}

export function getGuideCreationTimeSeries (guide) {
    const guideCreationDate = getGuideCreationDate(guide);

    return convertConfigToTimeSeries(
        {
            type: 'Custom',
            start: guideCreationDate.start,
            end: guideCreationDate.endPlusOneDay,
        },
        true,
    ).spec;
}

export function getGuideStatus ({ guide, restrictP1Access = true, classicDesignerTemplates = [] }) {
    if (!restrictP1Access) {
        if (classicDesignerTemplates && classicDesignerTemplates.length) {
            const steps = getStepsThatNeedReview({ guide, templates: classicDesignerTemplates });
            const needsReview = steps && steps.length > 0;
            if (needsReview) return 'needs review';
        }
    }

    if (guide.state === GUIDE_STATE.public && new Date(guide.showsAfter) > new Date()) return 'scheduled';
    if (guide.state === GUIDE_STATE.archived) return 'archive';

    return guide.state;
}

export function clearData (guideId) {
    return http.post(`/api/s/_SID_/guide/${guideId}/reset`, {});
}

export function getNoPublicEditPermissionTooltip (canChangeStatus) {
    return canChangeStatus
        ? $t(
            'noPublicEditPermissionTooltip.You do not have the right permissions to edit PUBLIC guides. Please change the guide status or reach out to your subscription admin for the relevant permissions.',
        )
        : $t(
            'noPublicEditPermissionTooltip.You do not have the right permissions to edit PUBLIC guides. Please reach out to your subscription admin for the relevant permissions.',
        );
}

export function hasProductLineAccess (guide, { hasValidate, hasNps }) {
    if (isNpsGuide(guide)) return hasNps;
    if (isIdeaPollGuide(guide)) return hasValidate;

    return true;
}

export function sendCreateMobileGuideTrackEvent (guideId, app, type) {
    const af = app?.mobileFrameworkFlavour;

    window.pendo.track('mobile_guide_created', {
        guideId,
        type,
        platform: app?.platform,
        framework: app?.framework,
        frameworkType: af?.type,
        sdkVersion: af?.sdkVersion,
        pluginVersion: af?.pluginVersion,
    });
}

export function isCarouselLayout (layout) {
    const buildingBlocks = get(layout, 'buildingBlocks');

    return !!BuildingBlock.findBlockByWidgetId(buildingBlocks, widgetIds.carousel);
}

export function isMobileTooltipLayout (isMobileApp, buildingBlocks) {
    return isMobileApp && !!buildingBlocks && !!BuildingBlock.findBlockByWidgetId(buildingBlocks, widgetIds.tooltip);
}

export function configureMobileGuide (layout, guide) {
    const layoutMigrations = get(layout, 'attributes.migrations', null);
    if (layoutMigrations) {
        guide.attributes.migrations = layoutMigrations;
        delete layout.attributes.migrations;
    }

    return guide;
}

export function configureAnnouncementGuide (guide) {
    guide.attributes.isAnnouncement = true;
    guide.launchMethod = 'launcher';
    guide.attributes.device = { type: 'all' };

    return guide;
}

export function configurePollsGuide (guide) {
    const polls = guide.steps.reduce((acc, step) => {
        const stepBuildingBlocks = step.buildingBlocks;
        if (!stepBuildingBlocks) return [];

        const pollObjects = Polls.generatePollsForBlocks(stepBuildingBlocks);
        if (pollObjects.length) {
            const pollIds = pollObjects.map((pollObject) => pollObject.id);
            step.pollIds = pollIds;
        }

        return acc.concat(pollObjects);
    }, []);

    if (polls.length) {
        guide.polls = polls;
    }

    return guide;
}

async function createStepFromLayout (guide, layout, theme = {}, carouselId, isEmbeddedLayout) {
    const isAnnouncement = get(guide, 'attributes.isAnnouncement');
    const step = await createStep({
        guide,
        type: 'building-block',
    });

    const containerId = `pendo-g-${guide.id}`;

    // For user layouts, we need to rebuild the guide with the guide id in the dom
    // now that the guide and guide id exist
    const mappings = {
        // We need to consolidate placeholder names at some point, but for now
        // we'll just map both to the same thing
        'CONTAINER_ID_HERE': containerId,
        'pendo-g-{THA_GUIDE_ID}': containerId,
        'pendo-g-GUIDE_ID_HERE': containerId,
    };

    const ids = StepCopy.getAllIds(layout.buildingBlocks, [
        'CONTAINER_ID_HERE',
        'pendo-g-{THA_GUIDE_ID}',
        'pendo-g-GUIDE_ID_HERE',
    ]);

    layout.buildingBlocks = StepCopy.copyBuildingBlocks(layout.buildingBlocks, ids, mappings, true).buildingBlocks;

    layout.dom = BuildingBlock.buildingBlocksToDom(layout.buildingBlocks);

    Object.assign(step, {
        layout: layout.id,
        buildingBlocks: layout.buildingBlocks,
        dom: layout.dom,
        guideId: guide.id,
        templateId: 'building-block-guide',
        ...(layout.htmlTemplate && { htmlTemplate: layout.htmlTemplate }),
        ...(layout.textTemplate && { textTemplate: layout.textTemplate }),
    });

    if (carouselId) {
        step.carouselId = carouselId;
    }

    const containerBlock = BuildingBlock.findBlockByDomId(step.buildingBlocks, 'pendo-guide-container');
    if (containerBlock && !get(containerBlock, 'web.themeStyle')) {
        containerBlock.web.themeStyle = 'container';
    }

    if (layout.attributes && layout.attributes.content) {
        const content = {};
        const contentForCombining = {};
        const { javascript, html } = splitContent(layout.attributes.content);

        Object.assign(contentForCombining, {
            css: layout.attributes.css,
            cssPrefix: `#${containerId}`,
            html,
            javascript,
        });

        const combinedContent = await combineContent(contentForCombining);
        content.content = combinedContent;
        Object.assign(content, {
            ...generateCspContent(step, combinedContent),
        });

        Object.assign(step, {
            content: content.content,
            contentCss: content.contentCss,
            contentJs: content.contentJs,
        });

        step.attributes.css = layout.attributes.css;
    }

    const layoutThemeId = get(layout, 'attributes.themeId', theme.id);
    step.attributes.themeId = isAnnouncement ? 'unthemed' : layoutThemeId;
    step.buildingBlocks = JSON.stringify(step.buildingBlocks);
    step.attributes.isAutoFocus = !isEmbeddedLayout;
    step.dom = JSON.stringify(step.dom);
    step.attributes.advanceActions = get(layout.attributes, 'advanceActions');
    step.attributes.blockOutUI = get(layout.attributes, 'blockOutUI');

    return step;
}

export async function createStepsFromLayout (guide, layout, theme, carouselId, isEmbeddedLayout) {
    theme = Array.isArray(theme) ? theme : [theme];
    const layoutSteps = BuildingBlockLayouts.getLayoutOrSteps(layout);
    const guideSteps = [];

    for (let index = 0; index < layoutSteps.length; index++) {
        const item = layoutSteps[index];
        const stepTheme = theme[index];
        const layoutStep = { id: layout.id, ...item };
        const workingGuide = { ...guide, steps: [...guide.steps, ...guideSteps] };
        // Process steps sequentially so they get the correct `rank` values assigned.
        // eslint-disable-next-line no-await-in-loop
        const step = await createStepFromLayout(workingGuide, layoutStep, stepTheme, carouselId, isEmbeddedLayout);
        guideSteps.push(step);
    }

    return guideSteps;
}

export async function addStepFromLayoutToGuide (guide, layout, theme, carouselId, stepIndex = 0, isEmbeddedLayout) {
    const guideSteps = await createStepsFromLayout(guide, layout, theme, carouselId, isEmbeddedLayout);
    guide.steps.splice(stepIndex, 0, ...guideSteps);

    return guide;
}

export function migrateCarouselSavedLayouts (layout) {
    BuildingBlockLayouts.forEachLayoutOrSteps(layout, ({ buildingBlocks }) => {
    // If the carousel has more than one indicator, we need to remove all but the first one
        const carouselIndicatorWrapper = BuildingBlock.findBlockByWidgetId(buildingBlocks, CAROUSEL_INDICATOR_WRAPPER);
        if (carouselIndicatorWrapper?.views?.length === 1) return;

        const carouselIndicator = carouselIndicatorWrapper?.views[0] || {};
        carouselIndicatorWrapper.views = [carouselIndicator];
        BuildingBlockProperties.updateOrCreateProperty(carouselIndicatorWrapper, 'selectedIndex', 'dimen', 0);
    });
}

/**
 *
 * @param {LayoutObject} layout
 * @param {Object} options
 * @param {Object || Array} options.customLayoutTheme theme object or array of theme objects
 * @param {Object || Array} options.theme fallback theme or array of themes to use if customLayoutTheme is not provided
 */
export async function createGuideFromLayout (
    layout,
    {
        app,
        isAnnouncement = false,
        isEmbeddedLayout = false,
        isEmailTransactional = false,
        guideName = 'Unnamed',
        guideDesctiption = '',
        theme = {},
        isEmail = false,
        isAppFlutterCodeless = false,
        isOrchestrate = false,
        isOrchestrateTemplate = false,
        location = '',
        maxPriority,
        customLayoutTheme = {},
        journeyId,
    },
) {
    customLayoutTheme = Array.isArray(customLayoutTheme) ? customLayoutTheme : [customLayoutTheme];
    theme = Array.isArray(theme) ? theme : [theme];
    const isOrgResource = layout?.isOrgLayout || false;
    const isMobile = isMobileApp(app.platform);
    const isCarousel = BuildingBlockLayouts.isCarouselLayout(layout);
    const carouselId = isCarousel ? uuid().substring(0, 8) : undefined;
    const carouselFirstStepLayoutId = CAROUSEL_FIRST_STEP_LAYOUT_ID;
    const layoutSteps = BuildingBlockLayouts.getLayoutOrSteps(layout);

    let guide = await buildGuide({
        guideType: 'building-block',
        app,
        maxPriority,
    });

    guide.steps = [];
    guide.attributes = guide.attributes || {};

    if (isMobile) {
        // Mobile app
        guide = configureMobileGuide(layout, guide);
    }

    if (isAnnouncement) {
        // Resource center announcement
        guide = configureAnnouncementGuide(guide);
    }

    if (isEmbeddedLayout) {
        // Embedded layout
        guide = configureEmbed(guide);
    }

    // if it is a saved carousel layout - we might need to migrate the layout
    if (isCarousel && layout.id !== carouselFirstStepLayoutId) {
        migrateCarouselSavedLayouts(layout);
    }

    guide = await addStepFromLayoutToGuide(guide, layout, theme, carouselId, 0, isEmbeddedLayout);
    guide.name = `${guideName}${isOrchestrateTemplate ? '' : ` ${createdDate()}`}`;
    guide.description = guideDesctiption;

    if (isCarousel && guide.steps[0].layout === carouselFirstStepLayoutId) {
        // If this is a carousel default layout - add 2 more steps to guide.
        guide = await addStepFromLayoutToGuide(
            guide,
            carouselLayouts._PENDO_DEFLYT_MobileCarouselSecondStep,
            theme,
            carouselId,
            1,
            isEmbeddedLayout,
        );
        guide = await addStepFromLayoutToGuide(
            guide,
            carouselLayouts._PENDO_DEFLYT_MobileCarouselThirdStep,
            theme,
            carouselId,
            2,
            isEmbeddedLayout,
        );
        guide.steps[1].activations[0].event = 'any';
        guide.steps[2].activations[0].event = 'any';
    }

    if (isEmail && isMobile) {
        guide.minSdkVersion = ROOT_MIN_SDK_VERSION;
    }

    if (layout.supportedPlatforms.mobile && !isEmail) {
        guide.minSdkVersion = getMinSdkVersionForGuide(
            {
                ...guide,
                steps: guide.steps.map(({ buildingBlocks }) => ({ buildingBlocks: JSON.parse(buildingBlocks) })),
            },
            undefined,
            app.platform,
            undefined,
            { isAppFlutterCodeless },
        );

        const type = isMobile && BuildingBlockLayouts.isTooltipLayout(layout) ? 'tooltip' : 'lightbox';

        sendCreateMobileGuideTrackEvent(guide.id, app, type);
    }

    // step.buildingBlocks need to be converted to JSON before being migrated
    guide.steps.forEach((step) => {
        if (step.buildingBlocks && isString(step.buildingBlocks)) {
            step.buildingBlocks = JSON.parse(step.buildingBlocks);
        }
    });

    guide = configurePollsGuide(guide);

    // We remove all the empty accessibilityTexts (if there are any) from all the radio buttons in mobile guides.
    if (layout.supportedPlatforms.mobile) {
        guide.steps.forEach((step) => {
            BuildingBlockPropertiesService.removeAllEmptyAccessibilityTexts(step.buildingBlocks);
        });
    }

    // These properties should be set before the migrations are run
    if (isEmail) {
        // Create email from journey or standalone email
        guide.messageTarget = EMAIL;
        guide.attributes.isEmail = true;
        guide.attributes.isEmailTransactional = isEmailTransactional;
    }

    // When a step created from a layout is added to a guide, the layout attributes are passed to the migration
    // function. Newly created layouts will have a shared services version that will be used to ensure the new
    // step receives all the necessary migrations. If the layout attributes have no shared service version
    // defined, the step/guide will be migrated according to a fixed version defined in the migrate function.
    BuildingBlockMigrations.migrate(guide, 'guide', layoutSteps[0].attributes);
    BuildingBlockMigrations.migrate(guide, 'guidesUnversioned');

    // migrations could change things like `themeStyle` so we want to make sure and re-apply the theme before we save them
    const useCustomLayout = layout.subtext === 'Custom Layout';
    const usedTheme = useCustomLayout ? customLayoutTheme : theme;

    // After migration, the step buildingBlocks need to be stringified so they can be saved properly
    guide.steps.forEach((step, idx) => {
        if (!isAnnouncement) {
            step.buildingBlocks = BuildingBlock.applyThemeToBuildingBlocks({
                theme: usedTheme[idx] || usedTheme[0],
                buildingBlocks: step.buildingBlocks,
                options: {
                    isEmail,
                    isEmbed: isEmbeddedLayout,
                },
            });
        }
        step.buildingBlocks = JSON.stringify(step.buildingBlocks);
    });

    if (isOrchestrate && !isEmail) {
        // Create guide from journey
        guide.attributes.overrideAutoThrottling = true;
        guide.attributes.journeyId = journeyId;
    }

    return { guide, isOrgResource, location };
}

export function canCreateGuide (appId) {
    return $can(appId)
        .entity('guide')
        .action('create')
        .source('ANY')
        .target('ANY');
}

export function canUseConversationalAI (segmentFlag, productLineMetadata, hasAIGuideConsent) {
    return segmentFlag && productLineMetadata && hasAIGuideConsent;
}

export async function normalizeUrl (url, preserveHttps) {
    if (!url) {
        const err = new Error('normalizeURL missing parameter `url`');

        return Promise.reject(err);
    }
    const cachedUrl = normalizedUrlMap[url];
    if (cachedUrl) return cachedUrl;

    const { data } = await http.post('/api/s/_SID_/helper/urlnorm', { url: encodeURI(url) });
    let normUrl = data;
    if (preserveHttps && isHTTPS(url)) {
        normUrl = normUrl.replace('http://', 'https://');
    }
    normalizedUrlMap[url] = normUrl;

    return normUrl;
}

export function isInV1Experiment (guide) {
    return guide.experiment && !guide.experiment.experimentId;
}

// If experiment status is Needs Review OR Active, then it's considered an active experiment
export function isInActiveExperiment (guide) {
    if (guide?.state !== 'public') return false;

    if (!guide?.experiment) return false;

    const currentTime = Date.now();
    const startTime = get(guide, 'experiment.startTime', 0);

    if (startTime === 0 || startTime > currentTime) return false;

    return true;
}

export function guideHasCustomCodeBlock (guide) {
    return guide.steps?.some((step) => step?.contentUrl || step?.contentUrlJs || step?.contentUrlCss);
}

/* End misc. helpers */
